import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Users, DollarSign, Award, ArrowLeft, Settings, Wallet,
  CheckCircle2, TrendingUp, Send, FileText, XCircle, Eye, Clock, Rocket,
  Trash2, User, AlertTriangle, Loader2, Shield, CreditCard
} from 'lucide-react';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSalesAgents() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCommission, setShowCommission] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [allCommissions, setAllCommissions] = useState([]);
  const [activeSection, setActiveSection] = useState('agents');
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [viewApp, setViewApp] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [onboardingProgress, setOnboardingProgress] = useState([]);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const [agentProfile, setAgentProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [newAgent, setNewAgent] = useState({
    name: '', email: '', password: '', phone: '', commission_rate: 0.08, monthly_target: 50000
  });

  const [commissionEdit, setCommissionEdit] = useState({ base_rate: 0.08, monthly_target: 50000 });
  const [payoutAmount, setPayoutAmount] = useState('');

  // Permissions state
  const [showPermissions, setShowPermissions] = useState(false);
  const [permAgent, setPermAgent] = useState(null);
  const [permData, setPermData] = useState({});
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/admin/sales-agents`, { headers });
      setAgents(res.data);
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCommissions = async () => {
    try {
      const res = await axios.get(`${API}/sales/commissions`, { headers });
      setAllCommissions(res.data);
    } catch (err) {
      setAllCommissions([]);
    }
  };

  const fetchApplications = async () => {
    try {
      const res = await axios.get(`${API}/admin/sales-applications`, { headers });
      setApplications(res.data);
    } catch (err) { setApplications([]); }
    finally { setAppsLoading(false); }
  };

  const fetchOnboardingProgress = async () => {
    try {
      const res = await axios.get(`${API}/admin/onboarding/progress`, { headers });
      setOnboardingProgress(res.data);
    } catch (err) { setOnboardingProgress([]); }
    finally { setOnboardingLoading(false); }
  };

  useEffect(() => {
    if (token && user?.role === 'admin') {
      fetchAgents();
      fetchAllCommissions();
      fetchApplications();
      fetchOnboardingProgress();
    }
  }, [token, user]);

  const handleApproveApplication = async (userId) => {
    try {
      await axios.post(`${API}/admin/sales-applications/${userId}/approve`, {}, { headers });
      toast.success('Application approved!');
      fetchApplications();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };

  const handleRejectApplication = async (userId) => {
    try {
      await axios.post(`${API}/admin/sales-applications/${userId}/reject`, { reason: rejectReason }, {
        headers: { ...headers, 'Content-Type': 'application/json' }
      });
      toast.success('Application rejected');
      setViewApp(null);
      setRejectReason('');
      fetchApplications();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
  };

  const handleCreateAgent = async () => {
    try {
      await axios.post(`${API}/admin/sales-agents`, newAgent, { headers });
      toast.success('Sales agent created!');
      setShowCreate(false);
      setNewAgent({ name: '', email: '', password: '', phone: '', commission_rate: 0.08, monthly_target: 50000 });
      fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create agent');
    }
  };

  const handleUpdateCommission = async () => {
    if (!selectedAgent) return;
    try {
      await axios.put(`${API}/admin/sales-agents/${selectedAgent.user_id}/commission`, commissionEdit, { headers });
      toast.success('Commission updated!');
      setShowCommission(false);
      fetchAgents();
    } catch (err) {
      toast.error('Failed to update commission');
    }
  };

  const handlePayout = async () => {
    if (!selectedAgent) return;
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) return;
    try {
      const res = await axios.post(`${API}/admin/sales-agents/${selectedAgent.user_id}/payout`, { amount }, { headers });
      toast.success(`$${amount.toFixed(2)} payout processed for ${selectedAgent.name}!`);
      setShowPayout(false);
      setPayoutAmount('');
      fetchAgents();
      fetchAllCommissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process payout');
    }
  };

  const fetchAgentProfile = async (agentId) => {
    setProfileLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sales-agents/${agentId}/profile`, { headers });
      setAgentProfile(res.data);
    } catch (err) {
      toast.error('Failed to load profile');
    } finally { setProfileLoading(false); }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await axios.delete(`${API}/admin/sales-agents/${agentId}`, { headers });
      toast.success('Agent deleted');
      setDeleteConfirm(null);
      fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete agent');
    }
  };

  const handleApproveCommission = async (txnId) => {
    try {
      await axios.post(`${API}/sales/commissions/${txnId}/approve`, {}, { headers });
      toast.success('Commission approved!');
      fetchAgents();
      fetchAllCommissions();
    } catch (err) {
      toast.error('Failed to approve commission');
    }
  };

  if (user?.role !== 'admin') {
    return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground">Admin access required</p></div>;
  }

  const pendingCommissions = allCommissions.filter(c => c.status === 'pending');
  const pendingApps = applications.filter(a => a.status === 'pending_review');

  const PERM_LABELS = {
    pipeline: { label: 'Pipeline', desc: 'View and manage sales pipeline stages' },
    leads: { label: 'My Leads', desc: 'Create and manage their own leads' },
    proposals: { label: 'Proposals', desc: 'Create and send proposals' },
    crm: { label: 'CRM', desc: 'Access global contacts and CRM tools' },
    ai_copilot: { label: 'AI Copilot', desc: 'Use AI-powered sales assistant' },
    email: { label: 'Email', desc: 'Send and manage email outreach' },
    calendar: { label: 'Calendar', desc: 'View and manage appointments' },
    sequences: { label: 'Sequences', desc: 'Automated email sequences' },
    commissions: { label: 'Commissions', desc: 'View earnings and payouts' },
    events: { label: 'Events', desc: 'View confirmed bookings and event workspace' },
    blog: { label: 'Blog Manager', desc: 'Create and manage blog posts' },
    staffing: { label: 'Event Staffing', desc: 'Manage staffing assignments' },
    marketing: { label: 'Marketing', desc: 'AI marketing page generator' },
    products: { label: 'Products', desc: 'View and manage service catalog' },
    workspaces: { label: 'Workspaces', desc: 'View active event workspaces' },
  };

  const openPermissions = async (agent) => {
    setPermAgent(agent);
    setShowPermissions(true);
    setPermLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sales-agents/${agent.user_id}/permissions`, { headers });
      setPermData(res.data.permissions || {});
    } catch { toast.error('Failed to load permissions'); }
    finally { setPermLoading(false); }
  };

  const savePermissions = async () => {
    setPermSaving(true);
    try {
      await axios.put(`${API}/admin/sales-agents/${permAgent.user_id}/permissions`, { permissions: permData }, { headers });
      toast.success(`Permissions updated for ${permAgent.name}`);
      setShowPermissions(false);
    } catch { toast.error('Failed to save permissions'); }
    finally { setPermSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="admin-sales-agents">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-muted-foreground">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground" data-testid="admin-sales-title">Sales Team</h1>
              <p className="text-muted-foreground text-sm">Manage agents, applications, commissions & payouts</p>
            </div>
          </div>
          {activeSection === 'agents' && (
            <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-agent-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Agent
            </Button>
          )}
        </div>

        {/* Section Tabs */}
        <div className="flex gap-2 mb-6" data-testid="section-tabs">
          <Button size="sm" variant={activeSection === 'agents' ? 'default' : 'outline'}
            onClick={() => setActiveSection('agents')}
            className={activeSection === 'agents' ? 'bg-indigo-600' : 'border-border text-muted-foreground'}>
            <Users className="h-3.5 w-3.5 mr-1.5" /> Active Agents ({agents.length})
          </Button>
          <Button size="sm" variant={activeSection === 'applications' ? 'default' : 'outline'}
            onClick={() => setActiveSection('applications')}
            className={activeSection === 'applications' ? 'bg-indigo-600' : 'border-border text-muted-foreground'}>
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Applications
            {pendingApps.length > 0 && (
              <Badge className="ml-1.5 bg-amber-500 text-slate-900 text-[9px] h-4 min-w-4 px-1">{pendingApps.length}</Badge>
            )}
          </Button>
          <Button size="sm" variant={activeSection === 'onboarding' ? 'default' : 'outline'}
            onClick={() => setActiveSection('onboarding')}
            className={activeSection === 'onboarding' ? 'bg-indigo-600' : 'border-border text-muted-foreground'}
            data-testid="onboarding-tab-btn">
            <Rocket className="h-3.5 w-3.5 mr-1.5" /> Onboarding ({onboardingProgress.length})
          </Button>
        </div>

        {/* ============ APPLICATIONS SECTION ============ */}
        {activeSection === 'applications' && (
          <div className="space-y-4">
            {appsLoading ? (
              <div className="text-center py-12"><div className="animate-pulse text-muted-foreground">Loading applications...</div></div>
            ) : applications.length === 0 ? (
              <Card className="bg-muted/60 border-border">
                <CardContent className="p-8 text-center">
                  <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No applications yet</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Pending Applications */}
                {pendingApps.length > 0 && (
                  <Card className="bg-amber-500/5 border-amber-500/20" data-testid="pending-applications">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-amber-400 flex items-center gap-1.5">
                        <Clock className="h-4 w-4" /> Pending Review ({pendingApps.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {pendingApps.map(app => (
                          <div key={app.application_id} className="flex items-center justify-between p-3 bg-muted/40 rounded-lg" data-testid={`app-${app.user_id}`}>
                            <div className="flex items-center gap-3">
                              {app.profile_photo_url ? (
                                <img src={app.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"><Users className="h-4 w-4 text-muted-foreground" /></div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-foreground">{app.full_name}</p>
                                <p className="text-[11px] text-muted-foreground">{app.email} | {app.coverage_area}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-indigo-400" onClick={() => setViewApp(app)} data-testid={`view-app-${app.user_id}`}>
                                <Eye className="h-3 w-3 mr-0.5" /> View
                              </Button>
                              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveApplication(app.user_id)} data-testid={`approve-app-${app.user_id}`}>
                                <CheckCircle2 className="h-3 w-3 mr-0.5" /> Approve
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400" onClick={() => { setViewApp(app); setRejectReason(''); }} data-testid={`reject-app-${app.user_id}`}>
                                <XCircle className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* All Applications Table */}
                <Card className="bg-muted/60 border-border">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-foreground">All Applications</CardTitle></CardHeader>
                  <CardContent className="p-0" data-testid="all-applications">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground text-xs">Applicant</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Coverage</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Experience</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Status</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Submitted</TableHead>
                          <TableHead className="text-muted-foreground text-xs">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {applications.map(app => (
                          <TableRow key={app.application_id} className="border-border/50 hover:bg-muted/30">
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {app.profile_photo_url ? (
                                  <img src={app.profile_photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-muted" />
                                )}
                                <div>
                                  <p className="text-sm font-medium text-foreground">{app.full_name}</p>
                                  <p className="text-[10px] text-muted-foreground">{app.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{app.coverage_area || '-'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{app.employment_history?.length || 0} jobs</TableCell>
                            <TableCell>
                              <Badge className={
                                app.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400 text-[10px]' :
                                app.status === 'rejected' ? 'bg-red-500/20 text-red-400 text-[10px]' :
                                'bg-amber-500/20 text-amber-400 text-[10px]'
                              }>{app.status.replace('_', ' ')}</Badge>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '-'}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-indigo-400" onClick={() => setViewApp(app)}>
                                <Eye className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* ============ ONBOARDING PROGRESS SECTION ============ */}
        {activeSection === 'onboarding' && (
          <div className="space-y-4" data-testid="admin-onboarding-section">
            {onboardingLoading ? (
              <div className="text-center py-12"><div className="animate-pulse text-muted-foreground">Loading onboarding data...</div></div>
            ) : onboardingProgress.length === 0 ? (
              <Card className="bg-muted/60 border-border">
                <CardContent className="p-8 text-center">
                  <Rocket className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No agents in onboarding yet</p>
                  <p className="text-muted-foreground text-xs mt-1">Onboarding begins when an agent's application is approved</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-muted/60 border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-foreground flex items-center gap-2">
                    <Rocket className="h-4 w-4 text-indigo-400" /> Agent Onboarding Progress
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground text-xs">Agent</TableHead>
                        <TableHead className="text-muted-foreground text-xs">Progress</TableHead>
                        <TableHead className="text-muted-foreground text-xs">Current Step</TableHead>
                        <TableHead className="text-muted-foreground text-xs">Steps</TableHead>
                        <TableHead className="text-muted-foreground text-xs">Status</TableHead>
                        <TableHead className="text-muted-foreground text-xs">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {onboardingProgress.map(agent => {
                        const pct = Math.round((agent.completed_steps / agent.total_steps) * 100);
                        const stepNames = ['Welcome', 'Profile', 'EU Cert'];
                        return (
                          <TableRow key={agent.user_id} className="border-border/50 hover:bg-muted/30" data-testid={`onboard-row-${agent.user_id}`}>
                            <TableCell>
                              <button onClick={() => fetchAgentProfile(agent.user_id)} className="text-left hover:text-indigo-400 transition-colors cursor-pointer" data-testid={`onboard-name-${agent.user_id}`}>
                                <p className="text-sm font-medium text-foreground hover:text-indigo-400">{agent.name}</p>
                                <p className="text-[10px] text-muted-foreground">{agent.email}</p>
                              </button>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${agent.completed ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="text-[10px] text-muted-foreground">{pct}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {agent.completed ? 'Done' : stepNames[agent.current_step - 1] || `Step ${agent.current_step}`}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-0.5">
                                {[1,2,3].map(i => (
                                  <div
                                    key={i}
                                    className={`w-5 h-5 rounded text-[9px] flex items-center justify-center font-bold ${
                                      agent.steps?.[String(i)]?.completed
                                        ? 'bg-emerald-500/20 text-emerald-400'
                                        : i === agent.current_step
                                        ? 'bg-indigo-500/20 text-indigo-400'
                                        : 'bg-muted text-muted-foreground'
                                    }`}
                                    title={stepNames[i-1]}
                                  >
                                    {agent.steps?.[String(i)]?.completed ? '\u2713' : i}
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={agent.completed ? 'bg-emerald-500/20 text-emerald-400 text-[10px]' : 'bg-amber-500/20 text-amber-400 text-[10px]'}>
                                {agent.completed ? 'Complete' : 'In Progress'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {!agent.completed && (
                                <div className="flex gap-1.5">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-[10px] px-2"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API}/admin/onboarding/${agent.user_id}/advance`, { action: 'advance' }, { headers });
                                        toast.success(`Advanced ${agent.name} to next step`);
                                        fetchOnboardingProgress();
                                      } catch (err) { toast.error('Failed to advance'); }
                                    }}
                                    data-testid={`advance-${agent.user_id}`}
                                  >
                                    Advance Step
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-7 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API}/admin/onboarding/${agent.user_id}/advance`, { action: 'complete' }, { headers });
                                        toast.success(`${agent.name} onboarding marked complete`);
                                        fetchOnboardingProgress();
                                      } catch (err) { toast.error('Failed to complete onboarding'); }
                                    }}
                                    data-testid={`complete-${agent.user_id}`}
                                  >
                                    <CheckCircle2 className="h-3 w-3 mr-0.5" /> Complete All
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ============ AGENTS SECTION ============ */}
        {activeSection === 'agents' && (
          <>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-muted/60 border-border">
            <CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Agents</p><p className="text-2xl font-bold text-foreground">{agents.length}</p></CardContent>
          </Card>
          <Card className="bg-muted/60 border-border">
            <CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Total Revenue</p><p className="text-2xl font-bold text-emerald-400">${agents.reduce((s, a) => s + (a.commission?.total_revenue || 0), 0).toLocaleString()}</p></CardContent>
          </Card>
          <Card className="bg-muted/60 border-border">
            <CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Commissions Earned</p><p className="text-2xl font-bold text-amber-400">${agents.reduce((s, a) => s + (a.commission?.total_earned || 0), 0).toFixed(2)}</p></CardContent>
          </Card>
          <Card className="bg-muted/60 border-border">
            <CardContent className="p-4"><p className="text-xs text-amber-400 mb-1">Pending Approvals</p><p className="text-2xl font-bold text-amber-400">{pendingCommissions.length}</p></CardContent>
          </Card>
        </div>

        {/* Pending Approvals */}
        {pendingCommissions.length > 0 && (
          <Card className="bg-amber-500/5 border-amber-500/20 mb-6" data-testid="pending-approvals">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-400">Pending Commission Approvals</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingCommissions.map(c => (
                  <div key={c.transaction_id} className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
                    <div>
                      <p className="text-sm text-foreground">{c.description}</p>
                      <p className="text-xs text-muted-foreground">{c.client_name} &middot; {new Date(c.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-emerald-400">${c.amount?.toFixed(2)}</span>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => handleApproveCommission(c.transaction_id)} data-testid={`approve-${c.transaction_id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agents Table */}
        <Card className="bg-muted/60 border-border">
          <CardContent className="p-0" data-testid="agents-table">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs">Agent</TableHead>
                  <TableHead className="text-muted-foreground text-xs">Tier</TableHead>
                  <TableHead className="text-muted-foreground text-xs text-right">Revenue</TableHead>
                  <TableHead className="text-muted-foreground text-xs text-right">Leads</TableHead>
                  <TableHead className="text-muted-foreground text-xs text-right">Won</TableHead>
                  <TableHead className="text-muted-foreground text-xs text-right">Earned</TableHead>
                  <TableHead className="text-muted-foreground text-xs text-right">Paid Out</TableHead>
                  <TableHead className="text-muted-foreground text-xs text-right">Balance</TableHead>
                  <TableHead className="text-muted-foreground text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map(agent => (
                  <TableRow key={agent.user_id} className="border-border/50 hover:bg-muted/30">
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        {agent.profile_photo_url ? (
                          <img src={agent.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-indigo-400" />
                          </div>
                        )}
                        <div>
                          <button onClick={() => fetchAgentProfile(agent.user_id)} className="text-sm font-medium text-foreground hover:text-indigo-400 transition-colors cursor-pointer text-left" data-testid={`agent-name-${agent.user_id}`}>
                            {agent.name}
                          </button>
                          <p className="text-[11px] text-muted-foreground">{agent.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 text-xs">
                        {agent.commission?.current_tier || 'Base'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm text-emerald-400 font-medium">${(agent.commission?.total_revenue || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm text-foreground">{agent.lead_count}</TableCell>
                    <TableCell className="text-right text-sm text-emerald-400">{agent.won_count}</TableCell>
                    <TableCell className="text-right text-sm text-amber-400">${(agent.commission?.total_earned || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">${(agent.commission?.total_paid || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm text-foreground font-medium">${(agent.wallet?.balance || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => fetchAgentProfile(agent.user_id)} title="View Profile" data-testid={`view-profile-${agent.user_id}`}>
                          <Eye className="h-3.5 w-3.5 text-indigo-400" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => {
                          setSelectedAgent(agent);
                          setCommissionEdit({
                            base_rate: agent.commission?.base_rate || 0.08,
                            monthly_target: agent.commission?.monthly_target || 50000,
                          });
                          setShowCommission(true);
                        }} title="Commission Settings">
                          <Settings className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-indigo-400" onClick={() => openPermissions(agent)} title="Permissions" data-testid={`permissions-btn-${agent.user_id}`}>
                          <Shield className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-400" onClick={() => {
                          setSelectedAgent(agent);
                          setPayoutAmount('');
                          setShowPayout(true);
                        }} disabled={!agent.wallet?.balance} title="Payout" data-testid={`payout-btn-${agent.user_id}`}>
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-300" onClick={() => setDeleteConfirm(agent)} title="Delete Agent" data-testid={`delete-agent-${agent.user_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        </>
        )}

        {/* Create Agent Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-card border-border max-w-md" data-testid="create-agent-dialog">
            <DialogHeader><DialogTitle className="text-foreground">Add Sales Agent</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Name *</Label><Input value={newAgent.name} onChange={e => setNewAgent(p => ({...p, name: e.target.value}))} className="bg-muted border-border h-9" data-testid="agent-name-input" /></div>
              <div><Label className="text-xs">Email *</Label><Input type="email" value={newAgent.email} onChange={e => setNewAgent(p => ({...p, email: e.target.value}))} className="bg-muted border-border h-9" data-testid="agent-email-input" /></div>
              <div><Label className="text-xs">Password *</Label><Input type="password" value={newAgent.password} onChange={e => setNewAgent(p => ({...p, password: e.target.value}))} className="bg-muted border-border h-9" data-testid="agent-password-input" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={newAgent.phone} onChange={e => setNewAgent(p => ({...p, phone: e.target.value}))} className="bg-muted border-border h-9" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Base Commission (%)</Label><Input type="number" step="0.01" value={newAgent.commission_rate} onChange={e => setNewAgent(p => ({...p, commission_rate: parseFloat(e.target.value) || 0}))} className="bg-muted border-border h-9" /></div>
                <div><Label className="text-xs">Monthly Target ($)</Label><Input type="number" value={newAgent.monthly_target} onChange={e => setNewAgent(p => ({...p, monthly_target: parseFloat(e.target.value) || 0}))} className="bg-muted border-border h-9" /></div>
              </div>
              <Button onClick={handleCreateAgent} className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!newAgent.name || !newAgent.email || !newAgent.password} data-testid="submit-create-agent">
                <Plus className="h-4 w-4 mr-1" /> Create Agent
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Commission Dialog */}
        <Dialog open={showCommission} onOpenChange={setShowCommission}>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader><DialogTitle className="text-foreground">Commission Settings — {selectedAgent?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Base Rate</Label><Input type="number" step="0.01" value={commissionEdit.base_rate} onChange={e => setCommissionEdit(p => ({...p, base_rate: parseFloat(e.target.value) || 0}))} className="bg-muted border-border h-9" /></div>
              <div><Label className="text-xs">Monthly Target ($)</Label><Input type="number" value={commissionEdit.monthly_target} onChange={e => setCommissionEdit(p => ({...p, monthly_target: parseFloat(e.target.value) || 0}))} className="bg-muted border-border h-9" /></div>
              <Button onClick={handleUpdateCommission} className="w-full bg-indigo-600 hover:bg-indigo-700"><CheckCircle2 className="h-4 w-4 mr-1" /> Save</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payout Dialog */}
        <Dialog open={showPayout} onOpenChange={setShowPayout}>
          <DialogContent className="bg-card border-border max-w-sm" data-testid="payout-dialog">
            <DialogHeader><DialogTitle className="text-foreground">Process Payout — {selectedAgent?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-muted-foreground">Available</p>
                  <p className="text-base font-bold text-emerald-400">${(selectedAgent?.wallet?.balance || 0).toFixed(2)}</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-muted-foreground">Earned</p>
                  <p className="text-base font-bold text-amber-400">${(selectedAgent?.commission?.total_earned || 0).toFixed(2)}</p>
                </div>
                <div className="bg-muted/50 border border-border rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-muted-foreground">Paid Out</p>
                  <p className="text-base font-bold text-muted-foreground">${(selectedAgent?.commission?.total_paid || 0).toFixed(2)}</p>
                </div>
              </div>
              <div><Label className="text-xs">Payout Amount ($)</Label><Input type="number" step="0.01" value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)} max={selectedAgent?.wallet?.balance || 0} className="bg-muted border-border h-9" data-testid="payout-amount-input" /></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPayoutAmount(String((selectedAgent?.wallet?.balance || 0).toFixed(2)))} className="text-xs flex-1">Pay All ${(selectedAgent?.wallet?.balance || 0).toFixed(2)}</Button>
                <Button variant="outline" size="sm" onClick={() => setPayoutAmount(String(((selectedAgent?.wallet?.balance || 0) / 2).toFixed(2)))} className="text-xs flex-1">Pay Half</Button>
              </div>
              <Button onClick={handlePayout} className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={!payoutAmount || parseFloat(payoutAmount) <= 0} data-testid="submit-payout">
                <Send className="h-4 w-4 mr-1" /> Process Payout ${payoutAmount ? parseFloat(payoutAmount).toFixed(2) : '0.00'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Application Detail Modal */}
        <Dialog open={!!viewApp} onOpenChange={() => { setViewApp(null); setRejectReason(''); }}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="application-detail-modal">
            {viewApp && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-foreground flex items-center gap-3">
                    {viewApp.profile_photo_url ? (
                      <img src={viewApp.profile_photo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-indigo-500/30" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-indigo-500/20 flex items-center justify-center">
                        <User className="h-6 w-6 text-indigo-400" />
                      </div>
                    )}
                    <div>
                      <p>{viewApp.full_name}</p>
                      <p className="text-xs text-muted-foreground font-normal">{viewApp.email} | {viewApp.phone}</p>
                    </div>
                    <Badge className={`ml-auto ${viewApp.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : viewApp.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {viewApp.status.replace('_', ' ')}
                    </Badge>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-[10px] text-muted-foreground">Address</p><p className="text-muted-foreground">{viewApp.address || '-'}</p></div>
                    <div><p className="text-[10px] text-muted-foreground">Birthday</p><p className="text-muted-foreground">{viewApp.birthday || '-'}</p></div>
                    <div><p className="text-[10px] text-muted-foreground">Coverage Area</p><p className="text-muted-foreground">{viewApp.coverage_area || '-'}</p></div>
                    <div><p className="text-[10px] text-muted-foreground">Submitted</p><p className="text-muted-foreground">{viewApp.submitted_at ? new Date(viewApp.submitted_at).toLocaleDateString() : '-'}</p></div>
                  </div>
                  {viewApp.why_join && (
                    <div><p className="text-[10px] text-muted-foreground mb-1">Why They Want to Join</p><p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">{viewApp.why_join}</p></div>
                  )}
                  {viewApp.employment_history?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Employment History</p>
                      <div className="space-y-2">
                        {viewApp.employment_history.map((e, i) => (
                          <div key={i} className="p-2 rounded-lg bg-muted/50 text-sm">
                            <p className="font-medium text-foreground">{e.title} at {e.company}</p>
                            <p className="text-[10px] text-muted-foreground">{e.start_date} - {e.end_date || 'Present'}</p>
                            {e.description && <p className="text-xs text-muted-foreground mt-1">{e.description}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {viewApp.education_history?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Education</p>
                      <div className="space-y-2">
                        {viewApp.education_history.map((e, i) => (
                          <div key={i} className="p-2 rounded-lg bg-muted/50 text-sm">
                            <p className="font-medium text-foreground">{e.degree} in {e.field}</p>
                            <p className="text-[10px] text-muted-foreground">{e.school} | {e.start_date} - {e.end_date || 'Present'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {viewApp.certifications?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Certifications</p>
                      <div className="flex flex-wrap gap-2">
                        {viewApp.certifications.map((c, i) => (
                          <Badge key={i} className="bg-amber-500/20 text-amber-400 text-xs">{c.name} — {c.issuer}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {viewApp.status === 'pending_review' && (
                    <div className="flex gap-2 pt-2 border-t border-border">
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => { handleApproveApplication(viewApp.user_id); setViewApp(null); }} data-testid="modal-approve-btn">
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <div className="flex-1 space-y-1">
                        <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Rejection reason (optional)" className="bg-muted border-border h-9 text-xs" />
                        <Button variant="destructive" className="w-full" onClick={() => handleRejectApplication(viewApp.user_id)} data-testid="modal-reject-btn">
                          <XCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Agent Profile Modal */}
        <Dialog open={!!agentProfile} onOpenChange={() => setAgentProfile(null)}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="agent-profile-modal">
            {profileLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-indigo-400" /></div>
            ) : agentProfile && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-foreground flex items-center gap-3">
                    {agentProfile.profile_photo_url || agentProfile.application?.profile_photo_url ? (
                      <img src={agentProfile.profile_photo_url || agentProfile.application?.profile_photo_url} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-indigo-500/30" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-indigo-500/20 flex items-center justify-center">
                        <User className="h-7 w-7 text-indigo-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-lg">{agentProfile.name}</p>
                      <p className="text-xs text-muted-foreground font-normal">{agentProfile.email}</p>
                      <Badge className={`mt-1 text-[10px] ${agentProfile.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {agentProfile.status || 'active'}
                      </Badge>
                    </div>
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-5 mt-3">
                  {/* Contact Info */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-[10px] text-muted-foreground uppercase">Phone</p>
                      <p className="text-sm font-medium text-foreground">{agentProfile.phone || '-'}</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-[10px] text-muted-foreground uppercase">Role</p>
                      <p className="text-sm font-medium text-foreground">{agentProfile.role}</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-[10px] text-muted-foreground uppercase">Joined</p>
                      <p className="text-sm font-medium text-foreground">{agentProfile.created_at ? new Date(agentProfile.created_at).toLocaleDateString() : '-'}</p>
                    </div>
                  </div>

                  {/* Financials */}
                  <div>
                    <p className="text-xs font-semibold text-foreground mb-2">Financial Summary</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-center">
                        <p className="text-[10px] text-muted-foreground">Revenue</p>
                        <p className="text-lg font-bold text-emerald-400">${(agentProfile.commission?.total_revenue || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-center">
                        <p className="text-[10px] text-muted-foreground">Earned</p>
                        <p className="text-lg font-bold text-amber-400">${(agentProfile.commission?.total_earned || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 text-center">
                        <p className="text-[10px] text-muted-foreground">Paid Out</p>
                        <p className="text-lg font-bold text-indigo-400">${(agentProfile.commission?.total_paid || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
                        <p className="text-[10px] text-muted-foreground">Balance</p>
                        <p className="text-lg font-bold text-foreground">${(agentProfile.wallet?.balance || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Performance */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-muted-foreground">Leads</p>
                      <p className="text-xl font-bold text-foreground">{agentProfile.lead_count || 0}</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-muted-foreground">Won</p>
                      <p className="text-xl font-bold text-emerald-400">{agentProfile.won_count || 0}</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-muted-foreground">Commission Rate</p>
                      <p className="text-xl font-bold text-foreground">{((agentProfile.commission?.base_rate || 0.08) * 100).toFixed(0)}%</p>
                    </div>
                  </div>

                  {/* Application Details */}
                  {agentProfile.application && (
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Application Details</p>
                      <div className="space-y-3 bg-muted/30 rounded-lg p-3 border border-border">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><p className="text-[10px] text-muted-foreground">Full Name</p><p className="text-foreground">{agentProfile.application.full_name || '-'}</p></div>
                          <div><p className="text-[10px] text-muted-foreground">Coverage Area</p><p className="text-foreground">{agentProfile.application.coverage_area || '-'}</p></div>
                          <div><p className="text-[10px] text-muted-foreground">Address</p><p className="text-foreground">{agentProfile.application.address || '-'}</p></div>
                          <div><p className="text-[10px] text-muted-foreground">Birthday</p><p className="text-foreground">{agentProfile.application.birthday || '-'}</p></div>
                        </div>
                        {agentProfile.application.why_join && (
                          <div><p className="text-[10px] text-muted-foreground mb-1">Why They Want to Join</p><p className="text-sm text-foreground bg-muted/50 p-2 rounded">{agentProfile.application.why_join}</p></div>
                        )}
                        {agentProfile.application.employment_history?.length > 0 && (
                          <div>
                            <p className="text-[10px] text-muted-foreground mb-1">Employment History</p>
                            {agentProfile.application.employment_history.map((e, i) => (
                              <div key={i} className="p-2 rounded bg-muted/50 text-sm mb-1">
                                <p className="font-medium text-foreground">{e.title} at {e.company}</p>
                                <p className="text-[10px] text-muted-foreground">{e.start_date} - {e.end_date || 'Present'}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {agentProfile.application.education_history?.length > 0 && (
                          <div>
                            <p className="text-[10px] text-muted-foreground mb-1">Education</p>
                            {agentProfile.application.education_history.map((e, i) => (
                              <div key={i} className="p-2 rounded bg-muted/50 text-sm mb-1">
                                <p className="font-medium text-foreground">{e.degree} in {e.field}</p>
                                <p className="text-[10px] text-muted-foreground">{e.school}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {agentProfile.application.certifications?.length > 0 && (
                          <div>
                            <p className="text-[10px] text-muted-foreground mb-1">Certifications</p>
                            <div className="flex flex-wrap gap-1">
                              {agentProfile.application.certifications.map((c, i) => (
                                <Badge key={i} className="bg-amber-500/20 text-amber-400 text-xs">{c.name} — {c.issuer}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recent Transactions */}
                  {agentProfile.transactions?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Recent Transactions</p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {agentProfile.transactions.map((txn, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm">
                            <div>
                              <p className="text-foreground text-xs">{txn.description || txn.type}</p>
                              <p className="text-[10px] text-muted-foreground">{txn.created_at ? new Date(txn.created_at).toLocaleDateString() : ''}</p>
                            </div>
                            <span className={`font-semibold text-xs ${txn.type === 'payout' ? 'text-red-400' : 'text-emerald-400'}`}>
                              {txn.type === 'payout' ? '-' : '+'}${(txn.amount || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" /> Delete Agent
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete <span className="font-semibold text-foreground">{deleteConfirm?.name}</span>?
              This will remove all their data including commissions, wallet, leads, and transactions. This cannot be undone.
            </p>
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-agent">Cancel</Button>
              <Button variant="destructive" size="sm" onClick={() => handleDeleteAgent(deleteConfirm?.user_id)} data-testid="confirm-delete-agent">Delete Agent</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Permissions Dialog */}
        <Dialog open={showPermissions} onOpenChange={setShowPermissions}>
          <DialogContent className="bg-card border-border max-w-md" data-testid="permissions-dialog">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <Shield className="h-5 w-5 text-indigo-400" /> Manage Access — {permAgent?.name}
              </DialogTitle>
            </DialogHeader>
            {permLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
            ) : (
              <div className="space-y-3">
                {Object.entries(PERM_LABELS).map(([key, { label, desc }]) => (
                  <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-muted/30" data-testid={`perm-${key}`}>
                    <div>
                      <p className="text-sm font-medium text-foreground">{label}</p>
                      <p className="text-[11px] text-muted-foreground">{desc}</p>
                    </div>
                    <Switch
                      checked={permData[key] !== false}
                      onCheckedChange={(checked) => setPermData(prev => ({ ...prev, [key]: checked }))}
                      data-testid={`perm-toggle-${key}`}
                    />
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowPermissions(false)}>Cancel</Button>
                  <Button size="sm" className="flex-1 bg-indigo-600" onClick={savePermissions} disabled={permSaving} data-testid="save-permissions">
                    {permSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Permissions'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
